// ============================================================================
// Department Report — orchestration hook
// ISOLATED MODULE. Reuses the EXISTING endpoints as-is (no backend change to
// them): /api/v1/users (list members + department), /api/v1/work-summary/stream
// (per-member generation), /api/v1/chat/stream (department roll-up).
//
// Server-safety by design:
//  - members are processed STRICTLY SEQUENTIALLY (no parallel fan-out)
//  - already-generated single-day summaries are reused via forceRegenerate=false
//  - the org AI credit limit is respected (AI_LIMIT_REACHED stops the run)
// ============================================================================

import { useState, useCallback, useRef } from 'react';
import { SERVER_URL } from '../../../utils/serverConfig';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

// Consume an SSE stream from one of the existing endpoints, accumulating the
// `content` chunks into a single string. Returns { text, status }.
async function consumeSummaryStream(body, signal) {
  const res = await fetch(`${SERVER_URL}/api/v1/work-summary/stream`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(body),
    signal,
  });

  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    if (errData?.error?.code === 'AI_LIMIT_REACHED') {
      const e = new Error('AI_LIMIT_REACHED');
      e.code = 'AI_LIMIT_REACHED';
      throw e;
    }
    throw new Error(`stream failed: ${res.status}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let text = '';
  let status = 'ok';
  const visualizations = [];

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      try {
        const data = JSON.parse(line.substring(6));
        if (data.type === 'content') text += data.text;
        else if (data.type === 'visualization') {
          // Mirror the per-user generator's visualization shape exactly.
          visualizations.push({
            chartType: data.chartType,
            title: data.title,
            data: data.data,
            config: data.config,
            columns: data.columns,
            nameKey: data.nameKey,
            valueKey: data.valueKey,
          });
        } else if (data.type === 'no_activity') { status = 'no_activity'; }
        else if (data.type === 'error') { status = 'error'; }
      } catch {
        /* ignore malformed SSE line */
      }
    }
  }
  return { text: text.trim(), status, visualizations };
}

// Roll-up pass: synthesize one concise department overview from member summaries.
// Uses the dedicated isolated /department-report/rollup endpoint (a plain text
// synthesis call), NOT the agentic /chat/stream pipeline.
async function generateDepartmentOverview(department, dateLabel, members, signal) {
  const payloadMembers = members
    .filter((m) => m.status === 'ok' && m.summary)
    .map((m) => ({ name: m.name, position: m.position, summary: m.summary }));

  if (!payloadMembers.length) {
    return 'No member activity was found for this department in the selected period.';
  }

  const res = await fetch(`${SERVER_URL}/api/v1/department-report/rollup`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ department, dateLabel, members: payloadMembers }),
    signal,
  });

  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    if (errData?.error?.code === 'AI_LIMIT_REACHED') {
      const e = new Error('AI_LIMIT_REACHED');
      e.code = 'AI_LIMIT_REACHED';
      throw e;
    }
    // Roll-up is best-effort; fall back gracefully without failing the report.
    return 'Department overview could not be generated; see per-member summaries below.';
  }

  const data = await res.json().catch(() => ({}));
  return data.overview || 'Department overview could not be generated; see per-member summaries below.';
}

export default function useDepartmentReport() {
  const [departments, setDepartments] = useState([]);
  const [membersByDept, setMembersByDept] = useState({});
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, label: '' });
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  // Load all org members and group them by (non-empty) department.
  const loadDepartments = useCallback(async () => {
    setLoadingDepartments(true);
    setError(null);
    try {
      const res = await fetch(`${SERVER_URL}/api/v1/users?limit=1000`, { headers: authHeaders() });
      const data = await res.json();
      const users = data?.data?.users || [];
      const grouped = {};
      users.forEach((u) => {
        const dept = (u.department || '').trim();
        if (!dept) return;
        if (!grouped[dept]) grouped[dept] = [];
        grouped[dept].push({
          id: u._id || u.id,
          name: `${u.firstName || ''} ${u.lastName || ''}`.trim(),
          email: u.email,
          position: u.position || '',
        });
      });
      setMembersByDept(grouped);
      setDepartments(Object.keys(grouped).sort());
    } catch (e) {
      setError('Failed to load departments.');
    } finally {
      setLoadingDepartments(false);
    }
  }, []);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    setRunning(false);
  }, []);

  // SINGLE-CALL mode: one combined backend call generates the whole department
  // report (one Qdrant query across all selected members + one AI pass). Fast
  // and cheap. `selectedIds` limits to a subset; when omitted, all members.
  const generate = useCallback(
    async (department, startDate, endDate, selectedIds = null, forceRegenerate = false) => {
      const allMembers = membersByDept[department] || [];
      const members = selectedIds
        ? allMembers.filter((m) => selectedIds.includes(m.id))
        : allMembers;
      if (!members.length) {
        setError('No members selected for that department.');
        return;
      }
      setRunning(true);
      setError(null);
      setReport(null);
      abortRef.current = new AbortController();
      const signal = abortRef.current.signal;
      setProgress({ current: 0, total: 1, label: forceRegenerate ? 'Regenerating…' : 'Analyzing department activity…' });

      try {
        const res = await fetch(`${SERVER_URL}/api/v1/department-report/generate`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({
            department,
            memberIds: members.map((m) => m.id),
            startDate,
            endDate,
            forceRegenerate,
          }),
          signal,
        });

        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          if (errData?.error?.code === 'AI_LIMIT_REACHED') {
            const e = new Error('AI_LIMIT_REACHED');
            e.code = 'AI_LIMIT_REACHED';
            throw e;
          }
          throw new Error(errData.message || `generate failed: ${res.status}`);
        }

        const data = await res.json();
        const r = data.report || {};
        setProgress({ current: 1, total: 1, label: 'Done' });
        setReport({
          department: r.department || department,
          startDate: r.startDate || startDate,
          endDate: r.endDate || endDate,
          headline: r.headline || '',
          sections: r.sections || null,
          memberHighlights: r.members || null, // structured per-member highlights
          overview: r.overview || '', // fallback raw text (only if JSON parse failed)
          empty: !!r.empty,
          members: [], // single-call mode: no full per-member summaries
          stats: r.stats || { memberCount: members.length, withData: 0 },
          cached: !!data.cached,
          _params: { department, startDate, endDate, selectedIds }, // for Regenerate
        });
        // Only a fresh generation consumes credits; a cache hit does not.
        if (!data.cached) window.dispatchEvent(new Event('ai-credit-used'));
      } catch (e) {
        if (e.code === 'AI_LIMIT_REACHED') {
          setError('AI credit limit reached. Resolve the limit and try again.');
        } else if (e.name === 'AbortError') {
          setError('Report generation cancelled.');
        } else {
          setError(e.message || 'Report generation failed.');
        }
      } finally {
        setRunning(false);
      }
    },
    [membersByDept],
  );

  /* ===========================================================================
   * LEGACY per-member loop (COMMENTED OUT — replaced by single-call /generate).
   * Kept for reference / quick rollback. Reuses consumeSummaryStream +
   * generateDepartmentOverview above.
   * ---------------------------------------------------------------------------
   * const generatePerMember = useCallback(
   *   async (department, startDate, endDate, selectedIds = null) => {
   *     const allMembers = membersByDept[department] || [];
   *     const members = selectedIds
   *       ? allMembers.filter((m) => selectedIds.includes(m.id))
   *       : allMembers;
   *     ... sequential loop calling consumeSummaryStream per member ...
   *     ... then generateDepartmentOverview(...) ...
   *   },
   *   [membersByDept],
   * );
   * ========================================================================= */

  return {
    departments,
    membersByDept,
    loadingDepartments,
    loadDepartments,
    running,
    progress,
    report,
    error,
    generate,
    cancel,
    setReport,
  };
}
